import React, { useState, useEffect } from 'react';

const GoogleImageDisplay = ({ keyword, className = '', style = {} }) => {
    const [imageUrl, setImageUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!keyword || keyword.trim() === '') {
            setImageUrl(null);
            setError(null);
            return;
        }

        const fetchImage = async () => {
            setLoading(true);
            setError(null);
            
            try {
                const response = await fetch(`/google_image_search?keyword=${encodeURIComponent(keyword)}&num_images=1`);
                const data = await response.json();
                
                if (data.success && data.images && data.images.length > 0) {
                    setImageUrl(data.images[0]);
                } else {
                    setError('No image found');
                    setImageUrl(null);
                }
            } catch (err) {
                console.error('Error fetching Google image:', err);
                setError('Failed to load image');
                setImageUrl(null);
            } finally {
                setLoading(false);
            }
        };

        // Debounce the API call to avoid too many requests
        const timeoutId = setTimeout(fetchImage, 300);
        return () => clearTimeout(timeoutId);
    }, [keyword]);

    const handleImageError = () => {
        setError('Image failed to load');
        setImageUrl(null);
    };

    const handleImageLoad = () => {
        setError(null);
    };

    if (!keyword || keyword.trim() === '') {
        return null;
    }

    return (
        <div className={`google-image-display ${className}`} style={style}>
            {loading && (
                <div className="image-loading">
                    <div className="loading-spinner"></div>
                    <span>Loading image...</span>
                </div>
            )}
            
            {error && !loading && (
                <div className="image-error">
                    <span>📷</span>
                    <span>{error}</span>
                </div>
            )}
            
            {imageUrl && !loading && !error && (
                <div className="image-container">
                    <img 
                        src={imageUrl} 
                        alt={keyword}
                        onError={handleImageError}
                        onLoad={handleImageLoad}
                        className="google-image"
                    />
                    <div className="image-caption">
                        {keyword}
                    </div>
                </div>
            )}
            
            <style jsx>{`
                .google-image-display {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    min-height: 220px;
                    background: transparent;
                    padding: 0;
                    position: relative;
                }
                
                .image-loading {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                    color: #888;
                    font-size: 12px;
                }
                
                .loading-spinner {
                    width: 20px;
                    height: 20px;
                    border: 2px solid #333;
                    border-top: 2px solid #666;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }
                
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                .image-error {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                    color: #666;
                    font-size: 11px;
                    text-align: center;
                }
                
                .image-error span:first-child {
                    font-size: 24px;
                    opacity: 0.5;
                }
                
                .image-container {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    width: 100%;
                    max-width: 100%;
                }
                
                .google-image {
                    max-width: 100%;
                    max-height: 200px;
                    object-fit: cover;
                    border-radius: 4px;
                    border: none;
                }
                
                .image-caption {
                    margin-top: 6px;
                    font-size: 10px;
                    color: #888;
                    text-align: center;
                    max-width: 100%;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                
                /* Responsive adjustments */
                @media (max-width: 768px) {
                    .google-image-display {
                        min-height: 180px;
                        padding: 0;
                    }
                    
                    .google-image {
                        max-height: 160px;
                    }
                }
            `}</style>
        </div>
    );
};

export default GoogleImageDisplay;
